<template>
  <!-- Refund Dialog -->
  <v-dialog
    v-if="showDialog"
    v-model="showDialog"
    max-width="500px"
    persistent
  >
    <v-card class="pa-4">
      <div>
        <v-card-title>
          <h2 class="title">Refund Payment</h2>
        </v-card-title>
        <div v-if="loading" class="my-4 px-4">
          <v-progress-linear
            :indeterminate="loading"
            :class="{ 'no-opacity': !loading }"
            style="height: 2px;"
          ></v-progress-linear>
        </div>
        <template v-if="!loading">
          <div class="pa-4">
            <v-alert
              :value="displayNote"
              dense
              outlined
              type="info"
              icon="info"
              class="mb-5"
            >
              <div class="body-2" style="color: rgba(0, 0, 0, 0.87)">
                If you wish to issue a partial refund for payment received on the present day, please do it after 10PM Eastern or some day in the future.
              </div>
            </v-alert>
            <v-flex xs12>
              <v-text-field
                label="Refund Amount*"
                v-model="preparedData.amount"
                placeholder="Please Enter"
                prepend-icon="attach_money"
                :error-messages="
                  errors($v.preparedData.amount) || server_errors.amount
                "
                @blur="$v.preparedData.amount.$touch()"
                @focus="resetServerValidation"
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-autocomplete
                :items="refund_reasons"
                label="Refund Reason*"
                item-text="title"
                item-value="id"
                v-model="preparedData.refund_reason_id"
                placeholder="Please Select"
                :error-messages="
                  errors($v.preparedData.refund_reason_id) ||
                    server_errors.refund_reason_id
                "
                @blur="$v.preparedData.refund_reason_id.$touch()"
                @focus="resetServerValidation"
              >
              </v-autocomplete>
            </v-flex>

            <v-flex xs12>
              <v-text-field
                label="Description"
                v-model="preparedData.refund_description"
                placeholder="Please Enter"
                :error-messages="server_errors.refund_description"
                @focus="resetServerValidation"
              ></v-text-field>
            </v-flex>
            <v-alert
              class="pa-2 mt-4"
              :value="$v.preparedData.$error"
              outlined
              color="error"
              icon="priority_high"
              transition="scale-transition"
            >
              <span class="caption">Please fill out form correctly</span>
            </v-alert>
            <v-alert
              class="pa-2 mt-4"
              :value="!serverValidation"
              outlined
              color="error"
              icon="priority_high"
              transition="scale-transition"
            >
              <span class="caption">{{ serverValidationMessage }}</span>
            </v-alert>
          </div>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click.native="closeModal"
              >Cancel</v-btn
            >
            <v-btn
              color="primary"
              :loading="saveLoading"
              :disabled="saveLoading"
              @click="refundPayment"
              >Save</v-btn
            >
          </v-card-actions>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/common/api.service";
import { validationMixin } from "vuelidate";
import { required, decimal, helpers } from "vuelidate/lib/validators";
import { valMessages, getValMessage } from "@/common/error-messages";
import { mapGetters, mapActions } from 'vuex'

const validations = {
  preparedData: {
    amount: {
      required,
      decimal,
      positive: v => !helpers.req(v) || v > 0,
      currency: v => !helpers.req(v) || /^([1-9][0-9]*)+(\.[0-9]{1,2})?$/.test(v) || /^0+(\.[0-9]{1,2})?$/.test(v) 
    },
    refund_reason_id: { required }
  }
};

export default {
  mixins: [validationMixin],
  validations,
  name: "RefundPayment",
  data: () => ({
    no_data: [null, "", undefined, "null"],
    showDialog: false,
    item: null,
    refund_reasons: [],
    preparedData: {},
    loading: false,
    saveLoading: false,
    serverValidation: true,
    serverValidationMessage: "Oops something went wrong...",
    server_errors: {},
    unsubscribe: null
  }),
  mounted() {
    this.unsubscribe = this.$store.subscribeAction(action => {
      if (action.type === 'vEmit' && action.payload.name === 'RefundPayment') {
          this.openModal(action.payload.data)
      }
    })
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {
    ...mapActions(['vEmit']),
    getRefundReasons() {
      this.loading = true
      return new Promise((resolve, reject) => {
        ApiService.get("/payment/refund_reasons")
          .then(response => {
            this.loading = false
            let data = response.data;
            this.refund_reasons = data.refund_reasons
            resolve(data);
          })
          .catch(({ response }) => {
            this.loading = false
            this.serverValidation = false;
            if (response.data.error) {
              this.serverValidationMessage = this.titleCase(
                response.data.error
              );
            }
            reject();
          });
      });
    },
    openModal(item) {
      this.item = item
      this.preparedData = {};
      this.preparedData.payment_id = item.id;
      this.showDialog = true;
      this.saveLoading = false
      this.resetServerValidation();
      this.$v.preparedData.$reset();
      this.getRefundReasons()
    },
    refundPayment() {
      this.resetServerValidation();
      this.$v.preparedData.$touch();
      if (!this.$v.preparedData.$invalid) {
        this.saveLoading = true;
        let body = this.preparedData;

        ApiService.put("/payment/refund", body)
          .then(response => {
            this.saveLoading = false;
            this.vEmit({ name: "notification", 
              data: {
                status: "success",
                text: "Specified amount is successfully refunded."
              }
            });
            this.closeModal()
            this.vEmit({ name: 'getClientInvoices' })
            this.vEmit({ name: 'getClientBalance' })
            this.$emit('done')
          })
          .catch(({ response }) => {
            this.saveLoading = false;
            this.serverValidation = false;
            if (response.data.error) {
              this.serverValidationMessage = this.titleCase(
                response.data.error
              );
            }
            if (response.data.message) {
              this.serverValidationMessage = response.data.message;
            }
            if (response.data.errors) {
              if (typeof response.data.errors === "string") {
                this.serverValidationMessage = response.data.errors;
              } else {
                this.server_errors = response.data.errors;
              }
            }
          });
      }
    },
    closeModal() {
        this.showDialog = false;
    },
    titleCase(str) {
      return _.startCase(str.replace(/_/g, " "));
    },
    resetServerValidation() {
      this.serverValidation = true;
      this.serverValidationMessage = "Oops something went wrong...";
      this.server_errors = {};
    },
    errors: getValMessage(valMessages)(validations)
  },
  computed: {
    ...mapGetters(["paymentProcessor","stripeCompleted"]),
    displayNote() {
      let processor = this.paymentProcessor === 'card_connect'
      let date = this.no_data.includes(this.item.date) || this.item.date === moment().format("YYYY[-]MM[-]DD")
      return processor && date
    }
  }
};
</script>

<style lang="stylus"></style>
